import ResourceList from '@/components/ResourceList'
import { basicColors } from '@/theme'
import { Box, Button, Typography } from '@material-ui/core'
import { useMemo, useState } from 'react'
import { Trash2 as TrashIcon, Edit as EditIcon, PlusCircle, Edit } from 'react-feather'
import ComponentConfiguration from './ComponentConfiguration'
import { v4 as uuidv4 } from 'uuid'

const defaultComponentConfiguration = {}

const CabinComponentManager = ({ components = [], assets = [], onChange }) => {
  const [componentConfiguration, setComponentConfiguration] = useState(null)

  const showConfiguration = componentConfiguration !== null
  const emptyCabin = components?.length === 0
  const configuredCabin = components?.length > 0

  const handleCreateConfiguration = () => {
    setComponentConfiguration(defaultComponentConfiguration)
  }

  const handleEditConfiguration = (configuration) => {
    setComponentConfiguration(configuration)
  }

  const handleRemoveComponent = (configId) => {
    if (configId) {
      const newComponentsList = components.filter(component => component.id !== configId)
      onChange(newComponentsList)
    }
  }

  const handleAddComponent = () => {
    const newComponentConfiguration = { ...componentConfiguration, id: uuidv4() }
    onChange([...components, newComponentConfiguration])
    setComponentConfiguration(null)
  }

  const handleEditComponent = () => {
    onChange(components.map(c => c.id === componentConfiguration.id ? componentConfiguration : c))
    setComponentConfiguration(null)
  }

  const resourceList = useMemo(() => {
    return components.map(component => ({
      id: component.id,
      name: component.assetName,
      assetId: component.assetId,
      type: component.assetType
    }))
  }, [components])

  const availableAssets = useMemo(() => {
    const assetIds = components.map(component => component.assetId)
    const availableAssetIds = componentConfiguration?.id ? assetIds.filter(assetId => assetId !== componentConfiguration.assetId) : assetIds
    return assets.filter(asset => !availableAssetIds.includes(asset.uuid))
  }, [assets, components, componentConfiguration])

  if (showConfiguration) {
    return (
      <>
        <ComponentConfiguration config={componentConfiguration} setConfig={setComponentConfiguration} assets={availableAssets} />
        <Box mt={2} display='flex' justifyContent='flex-end'>
          <Button
            size='small'
            color='secondary'
            variant='contained'
            disabled={!componentConfiguration?.assetId}
            startIcon={componentConfiguration?.id ? <Edit size={16} /> : <PlusCircle size={16} />}
            onClick={componentConfiguration?.id ? handleEditComponent : handleAddComponent}
          >
            {componentConfiguration?.id ? 'Modifica componente' : 'Aggiungi componente'}
          </Button>
        </Box>
      </>
    )
  }

  return (
    <div>
      {emptyCabin && (
        <>
          <Typography variant='h6'>Aggiungi componenti alla cabina</Typography>
          <Box width='100%' mt={2}>
            <Button fullWidth startIcon={<PlusCircle />} onClick={handleCreateConfiguration}>Aggiungi componente alla cabina</Button>
          </Box>
        </>
      )}
      {configuredCabin && (
        <Box minHeight={340} mt={2} style={{ border: '1px solid #182638', borderRadius: 8, overflow: 'hidden' }}>
          <ResourceList
            customHeader={() => (
              <Box width='100%' display='flex' alignItems='center' justifyContent='space-between'>
                <Typography variant='h6'>Aggiungi componenti alla cabina</Typography>
                <Button size='small' color='secondary' variant='contained' startIcon={<PlusCircle />} onClick={handleCreateConfiguration}>Nuovo</Button>
              </Box>
            )}
            fields={[{ value: 'name', label: 'Nome' }, { value: 'type', label: 'Tipo' }]}
            emptyMessage='Non ci sono componenti nella cabina'
            list={resourceList}
            filterBy={['name', 'type']}
            actions={[
              {
                title: 'Modifica componente',
                icon: <EditIcon color={basicColors.darkYellow} />,
                action: (prevConfig) => {
                  const config = components.find(component => component.id === prevConfig?.id)
                  handleEditConfiguration(config)
                }
              },
              {
                title: 'Rimuovi componente',
                icon: <TrashIcon color={basicColors.darkRed} />,
                action: (prevConfig) => {
                  const configId = prevConfig?.id
                  handleRemoveComponent(configId)
                }
              }
            ]}
          />
        </Box>
      )}
    </div>
  )
}

export default CabinComponentManager
